// src/pages/LandingPage.js
import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Stack,
  Divider,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  Link as MuiLink, // Renamed to avoid conflict
  IconButton,
  Snackbar,
  Alert,
  CircularProgress 
} from '@mui/material';
import { 
  Place, 
  CameraAlt, 
  Description,
  Search,
  Star,
  Payments,
  VerifiedUser,
  HowToReg,
  Assignment,
  CheckCircle,
  Facebook,
  Twitter,
  Instagram,
  LinkedIn
} from '@mui/icons-material';
import { Link } from 'react-router-dom'; // For navigation
import Header from '../components/Header';

export default function LandingPage() {

  // ADD NEWSLETTER STATES
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' or 'error'
  });

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    // Clear error when user starts typing
    if (emailError && validateEmail(value)) {
      setEmailError('');
    }
  };

  // Handle subscription
  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    // Validate email format
    if (!email) {
      setEmailError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    // Clear any previous errors
    setEmailError('');
    setSubscriptionLoading(true);
    
    try {
      const response = await fetch('https://propertyearn.net/api/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Success
        setSnackbar({
          open: true,
          message: data.message || 'Successfully subscribed to newsletter!',
          severity: 'success'
        });
        setEmail(''); // Clear input
      } else {
        // Error
        setSnackbar({
          open: true,
          message: data.error || 'Subscription failed. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setSnackbar({
        open: true,
        message: 'Network error. Please check your connection.',
        severity: 'error'
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  return (
    <Box sx={{ overflowX: 'hidden' }}>
      <Header />
      
      {/* ===== Hero Section ===== */}
      <Box sx={{
        backgroundImage: 'url(/property-hero.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }
      }}>
        <Container maxWidth="lg" sx={{ 
          position: 'relative', 
          zIndex: 1,
          textAlign: 'center',
          color: 'white'
        }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Earn Money Completing Property Tasks
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 5,
              maxWidth: '800px',
              mx: 'auto'
            }}
          >
            Get paid for verifying listings, taking photos, and more. No experience required.
          </Typography>
          
          <Paper 
            component="form" 
            sx={{ 
              maxWidth: '800px',
              mx: 'auto',
              p: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Search sx={{ color: 'action.active', mr: 1 }} />
            <InputBase
              placeholder="Search tasks by location..."
              sx={{ ml: 1, flex: 1 }}
            />
            <Divider orientation="vertical" sx={{ height: 28, mx: 2 }} />
            <Button 
              variant="contained" 
              color="primary"
              sx={{ 
                px: 4,
                whiteSpace: 'nowrap'
              }}
            >
              Find Tasks
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* ===== How It Works ===== */}
        <Container 
  maxWidth="lg" 
  sx={{ py: 10, textAlign: 'center' }}
  id="how-it-works-section" // ADD THIS ID
>
  <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 6 }}>
    How It Works
  </Typography>
        <Grid container spacing={4} justifyContent="center">
          {[
            { 
              icon: <HowToReg fontSize="large" />, 
              title: "Sign Up", 
              text: "Create your free account in 30 seconds" 
            },
            { 
              icon: <Assignment fontSize="large" />, 
              title: "Pick Tasks", 
              text: "Choose from verifications, photos, surveys" 
            },
            { 
              icon: <Payments fontSize="large" />, 
              title: "Get Paid", 
              text: "Cash out via PayPal, bank, or crypto" 
            }
          ].map((step, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Card sx={{
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                transition: 'transform 0.3s',
                '&:hover': { transform: 'translateY(-8px)' }
              }}>
                <Box sx={{ 
                  bgcolor: 'primary.light', 
                  width: 80, 
                  height: 80,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  color: 'primary.main'
                }}>
                  {step.icon}
                </Box>
                <Typography variant="h5" gutterBottom>
                  {step.title}
                </Typography>
                <Typography color="text.secondary">
                  {step.text}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== Featured Tasks ===== */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom 
            sx={{ fontWeight: 600, mb: 6 }}
          >
            Featured Tasks
          </Typography>
          <TableContainer 
            component={Paper} 
            elevation={0} 
            sx={{ 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              mx: 'auto',
              maxWidth: 800
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white' }}>Task</TableCell>
                  <TableCell align="right" sx={{ color: 'white' }}>Earnings</TableCell>
                  <TableCell align="right" sx={{ color: 'white' }}>Time Required</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { 
                    icon: <Place color="primary" />, 
                    task: 'Verify Property Address', 
                    earnings: '1,000 - £2,000', 
                    time: '15 mins' 
                  },
                  { 
                    icon: <CameraAlt color="primary" />, 
                    task: 'Upload Video Tour', 
                    earnings: '£2,000 - £4,000', 
                    time: '30 mins' 
                  },
                  { 
                    icon: <Search color="primary" />, 
                    task: 'Report Rental Prices', 
                    earnings: '£700 - £1,500', 
                    time: '5 mins' 
                  }
                ].map((row, i) => (
                  <TableRow 
                    key={i} 
                    hover 
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {row.icon}
                        <span>{row.task}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                      {row.earnings}
                    </TableCell>
                    <TableCell align="right">{row.time}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
       {/* MODIFIED: View All Tasks Button - Now links to login page */}
<Box textAlign="center" sx={{ mt: 4 }}>
  <Button 
    variant="outlined" 
    color="primary" 
    size="large"
    component={Link} // ADD THIS: Makes button act as a link
    to="/login" // ADD THIS: Destination route
    sx={{
      textDecoration: 'none', // Remove default link styling
      '&:hover': {
        backgroundColor: 'primary.main',
        color: 'white',
        transform: 'translateY(-2px)',
        boxShadow: 2
      },
      transition: 'all 0.2s ease-in-out'
    }}
  >
    View All Tasks
  </Button>
</Box>
        </Container>
      </Box>

      {/* ===== Why Choose Us ===== */}
      <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 6 }}>
          Why Choose Us?
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 4, height: '100%' }}>
              <List>
                {[
                  "Instant Payouts",
                  "Flexible Work Hours",
                  "No Hidden Fees",
                  "24/7 Support",
                  "Verified Property Owners"
                ].map((item) => (
                  <ListItem key={item} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <Typography>{item}</Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              bgcolor: 'primary.light', 
              p: 4,
              borderRadius: 2,
              height: '100%'
            }}>
              <Typography variant="h5" gutterBottom sx={{ color: 'primary.main' }}>
                Trusted by Thousands
              </Typography>
              <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
                10,000+
              </Typography>
              <Typography>
                Active taskers earning with us weekly
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ===== Testimonials ===== */}
      <Container maxWidth="lg" sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 6 }}>
          What Our Taskers Say
        </Typography>
        <Grid container spacing={4} justifyContent="center">
          {[
            {
              quote: "I earned over £7,500 last month just doing property verifications in my spare time!",
              author: "Olivia T., London",
              rating: 5
            },
            {
              quote: "The perfect side hustle. I choose tasks near my office and complete them during lunch.",
              author: "Sarah K., Manchester",
              rating: 5
            },
            {
              quote: "Payments arrive faster than any other gig platform I've used. Highly recommend!",
              author: "David R., London",
              rating: 4
            }
          ].map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Paper sx={{ 
                p: 4,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      color={i < testimonial.rating ? "primary" : "disabled"} 
                    />
                  ))}
                </Box>
                <Typography variant="h6" paragraph sx={{ fontStyle: 'italic' }}>
                  "{testimonial.quote}"
                </Typography>
                <Typography color="text.secondary" mt="auto">
                  — {testimonial.author}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ===== Final CTA ===== */}
      <Box sx={{ 
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(/property-cta.jpg)',
        backgroundSize: 'cover',
        py: 15,
        textAlign: 'center',
        color: 'white'
      }}>
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              mb: 3
            }}
          >
            Ready to Start Earning?
          </Typography>
          <Typography variant="h5" sx={{ mb: 5 }}>
            Join thousands of taskers making money in their spare time
          </Typography>
          <Button 
  variant="contained" 
  color="primary" 
  size="large"
  component={Link} // ADD THIS: Makes button act as a link
  to="/signup" // ADD THIS: Destination route
  sx={{ 
    px: 8,
    py: 2,
    fontSize: '1.1rem',
    fontWeight: 600,
    textDecoration: 'none', // Remove default link styling
    '&:hover': {
      backgroundColor: 'primary.dark',
      transform: 'translateY(-2px)',
      boxShadow: 4
    },
    transition: 'all 0.2s ease-in-out'
  }}
>
  Sign Up Free
          </Button>
        </Container>
      </Box>
      
{/* ===== Footer ===== */}
<Box component="footer" sx={{ 
  bgcolor: 'primary.dark',
  color: 'white',
  py: 8
}}>
  <Container maxWidth="lg">
    <Grid container spacing={4}>
      {/* Column 1: Brand Info - Reduced width */}
      <Grid item xs={12} sm={6} md={2.5}>
        <Typography variant="h6" gutterBottom>
          PropertyEarn
        </Typography>
        <Typography paragraph sx={{ mb: 2 }}>
          The easiest way to earn money through property-related tasks.
        </Typography>
        <Stack direction="row" spacing={2}>
          {[
            { icon: <Facebook />, name: 'Facebook' },
            { icon: <Twitter />, name: 'Twitter' },
            { icon: <Instagram />, name: 'Instagram' },
            { icon: <LinkedIn />, name: 'LinkedIn' }
          ].map((social) => (
            <IconButton 
              key={social.name} 
              sx={{ color: 'white' }}
            >
              {social.icon}
            </IconButton>
          ))}
        </Stack>
      </Grid>

      {/* Column 2: Company Links - Reduced width */}
      <Grid item xs={6} sm={3} md={1.8}>
        <Typography variant="h6" gutterBottom>
          Company
        </Typography>
        <List dense sx={{ py: 0 }}>
          {['About', 'Careers', 'Blog', 'Contact'].map((item) => (
            <ListItem key={item} disablePadding sx={{ mb: 1 }}>
              <MuiLink href="#" color="inherit" underline="hover" sx={{ fontSize: '0.9rem' }}>
                {item}
              </MuiLink>
            </ListItem>
          ))}
        </List>
      </Grid>

      {/* Column 3: Resources - Reduced width */}
      <Grid item xs={6} sm={3} md={1.8}>
        <Typography variant="h6" gutterBottom>
          Resources
        </Typography>
        <List dense sx={{ py: 0 }}>
          {['Help Center', 'FAQ', 'Terms', 'Privacy'].map((item) => (
            <ListItem key={item} disablePadding sx={{ mb: 1 }}>
              <MuiLink href="#" color="inherit" underline="hover" sx={{ fontSize: '0.9rem' }}>
                {item}
              </MuiLink>
            </ListItem>
          ))}
        </List>
      </Grid>

      {/* Column 4: Quick Links - Reduced width */}
      <Grid item xs={6} sm={3} md={1.8}>
        <Typography variant="h6" gutterBottom>
          Quick Links
        </Typography>
        <List dense sx={{ py: 0 }}>
          {[ 'Post a Task', 'How It Works', 'Earnings'].map((item) => (
            <ListItem key={item} disablePadding sx={{ mb: 1 }}>
              <MuiLink href="#" color="inherit" underline="hover" sx={{ fontSize: '0.9rem' }}>
                {item}
              </MuiLink>
            </ListItem>
          ))}
        </List>
      </Grid>

          {/* Column 5: Newsletter - Updated with validation */}
        <Grid item xs={12} sm={6} md={3.1}>
          <Box component="form" onSubmit={handleSubscribe}>
            <Typography variant="h6" gutterBottom>
              Newsletter
            </Typography>
            <Typography paragraph sx={{ mb: 2 }}>
              Stay updated with our latest offers
            </Typography>
            <Stack direction="row" spacing={1}>
              <InputBase
                placeholder="Your email"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                disabled={subscriptionLoading}
                sx={{ 
                  bgcolor: 'white',
                  px: 2,
                  borderRadius: 1,
                  flex: 1,
                  color: 'black',
                  height: 40,
                  border: emailError ? '1px solid red' : 'none'
                }}
              />
              <Button 
                variant="contained" 
                color="secondary"
                type="submit"
                disabled={subscriptionLoading || !email}
                sx={{ 
                  whiteSpace: 'nowrap',
                  height: 40,
                  px: 2,
                  minWidth: '100px'
                }}
              >
                {subscriptionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Subscribe'
                )}
              </Button>
            </Stack>
            {/* Error message display */}
            {emailError && (
              <Typography 
                color="error" 
                variant="caption" 
                sx={{ 
                  mt: 1, 
                  display: 'block',
                  color: '#ff6b6b'
                }}
              >
                {emailError}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

    <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />
    <Typography align="center">
      © 2022 PropertyEarn. All rights reserved.
    </Typography>
  </Container>
</Box>

    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseSnackbar} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
</Box>
  );
}